// @flow

import React, { useEffect } from "react";
import Select, { createFilter, components, type RSTypes } from "react-select";
import { MultiSelectOption, MultiSelectValue } from "./components.js";

export type OptionType = string | number | boolean | null | { value: any, label: string };

export type ValueOptionType = OptionType;
type Props = $Exact<{
  options: Array<OptionType>,
  value?: ValueOptionType | Array<ValueOptionType>,
  defaultValue?: ValueOptionType | Array<ValueOptionType>,
  onChange?: Function,
  disabled?: boolean,
  readOnly?: boolean,
  className?: string,
  placeholder?: string,
  autoFocus?: boolean,
  id?: string,
  isMulti?: boolean,
  withoutPortal?: boolean,
  withoutEmptyOption?: boolean,
  menuIsOpen?: boolean,
  menuPortalTarget?: HTMLElement,
  menuPlacement?: "bottom" | "auto" | "top",
  ...RSTypes,
}>;

const customStyles = {
  // https://react-select.com/styles#style-object
  menuPortal: (provided: Object) => ({
    ...provided,
    zIndex: 1060,
  }),
  option: (provided: Object, state: Object) => {
    return {
      ...provided,
      color: state.label?.indexOf && state.label.indexOf(" - Inactive") !== -1 ? "red" : "#6F6F6F", //Sorry but AU-7433
    };
  },
};

function ComboBox(props: Props) {
  const menuPortalTarget = props.hasOwnProperty("menuPortalTarget")
    ? props.menuPortalTarget
    : document.body;

  function handleChange(selectedOption: OptionType | Array<OptionType>) {
    if (props.isMulti && Array.isArray(selectedOption)) {
      return (
        // $FlowIgnore
        props.onChange && props.onChange(selectedOption.map((opt: OptionType) => opt.value))
      );
    } else {
      // $FlowIgnore
      return props.onChange && props.onChange(selectedOption?.value);
    }
  }

  function options(): Array<OptionType> {
    const opts = props.options.map(opt => {
      if (typeof opt === "object") {
        let label;
        let value;

        if (opt.hasOwnProperty("value")) {
          value = opt.value;
        } else if (opt.hasOwnProperty("id")) {
          value = opt.id;
        } else {
          value = "";
        }

        if (opt.hasOwnProperty("label")) {
          label = opt.label;
        } else if (opt.hasOwnProperty("name")) {
          label = opt.name;
        } else {
          label = value;
        }

        return { ...opt, label: label, value: value };
      }
      return {
        label: `${opt}`,
        value: opt,
      };
    });

    if (
      opts &&
      !props.isMulti &&
      !props.withoutEmptyOption &&
      !opts.find(opt => opt.value === props.value)
    ) {
      return [{ label: "", value: null }, ...opts];
    }
    return opts || [];
  }

  function selectedValue() {
    if (props.isMulti && !!props.value) {
      return props.value.map(val => {
        if (typeof val == "object" && val.label && val.value)
          return { ...val, label: `${val.label}` };
        else if (typeof val != "object")
          return {
            label: `${val}`,
            value: val,
          };
      });
    } else {
      return typeof props.value === "object"
        ? props.value // $FlowIgnore
        : options().find(el => el.value === props.value);
    }
  }

  return (
    <Select
      classNamePrefix="combo-box"
      className={`combo-box-root ${(props.readOnly && "readonly") || ""} ${props.className || ""}`}
      styles={customStyles}
      backspaceRemovesValue={false}
      isDisabled={props.disabled}
      readOnly={props.readOnly}
      onChange={handleChange}
      value={selectedValue()}
      options={options()}
      inputId={props.id}
      name={props.name}
      autoFocus={props.autoFocus}
      isMulti={props.isMulti || false}
      isClearable={false}
      placeholder={props.placeholder}
      closeMenuOnSelect={!props.isMulti}
      hideSelectedOptions={!props.isMulti}
      menuIsOpen={props.menuIsOpen}
      menuPortalTarget={!props.withoutPortal ? menuPortalTarget : null}
      menuPlacement="auto"
      // menuPosition={"fixed"} // TODO: check with these options in order to fix the portalBehaviour
      menuShouldScrollIntoView={true}
      maxMenuHeight={200}
      components={{
        Option: props.isMulti ? MultiSelectOption : components.Option,
        MultiValueContainer: MultiSelectValue,
      }}
    />
  );
}

export default ComboBox;
